'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Collapse, Stack } from '@mui/material'
import { usePathname } from 'next/navigation'
import classNames from 'classnames'
import Link from '@biolumina/ui/components/link/link'
import { matchNavPath } from '@biolumina/ui/util/stringUtil'
import { StyledMobileMenu } from './mobileMenu.styles'
import { useGlobalState } from '@biolumina/ui/context/globalStateProvider'

export default function MobileMenu({
  data,
  isOpen,
  onToggle: pOnToggle,
  onNavToggle,
  onInit,
}) {
  const pathname = usePathname()
  const isCurrent = pathname.replaceAll('/', '') === data.href.replaceAll('/', '')
  const { scrollSpyIndex } = useGlobalState()

  const {
    display: navDisplay,
    mobileDisplay,
    menuAlign,
    href = '',
    className,
    icon: Icon,
    ...navData
  } = data

  const label = mobileDisplay || navDisplay
  const [childIsActive, setChildIsActive] = useState(false)
  const isActive = useMemo(() => isOpen || isCurrent, [isOpen, isCurrent])

  const onToggle = useCallback(() => {
    pOnToggle?.(href)
  }, [pOnToggle, href])

  const handleNavigation = useCallback(() => {
    onNavToggle?.()
  }, [onNavToggle])

  const onHash = useCallback((hash) => () => {
    document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' })
    handleNavigation()
  }, [handleNavigation])

  useEffect(() => {
    if (!navData.children) return
    const matches = navData.children.filter((child) =>
      matchNavPath(pathname, child.href, true)
    )
    setChildIsActive(matches.length > 0)
  }, [navData.children, pathname])

  useEffect(() => {
    if (pathname.replaceAll('/', '') === href.replaceAll('/', '') && onInit) {
      onInit(href)
    }
  }, [onInit, href, pathname])

  return (
    <StyledMobileMenu>
      <Link
        className={classNames('nav-link', className, {
          active: isActive || childIsActive,
        })}
        variant="nav"
        href={navData.children ? null : href}
        onClick={navData.children ? onToggle : handleNavigation}
        {...navData}
      >
        {label}
        {Icon && <Icon />}
        {navData.children &&
          (isOpen || childIsActive ? <RemoveCircleIcon /> : <AddCircleIcon />)}
      </Link>

      {navData.children && (
        <Collapse in={isOpen || childIsActive} unmountOnExit>
          <Stack direction="column" spacing={0} className="sub-nav">
            {navData.children.map(({ display, mobileDisplay, ...navItem }, index) => {
              const label = mobileDisplay || display
              const hash = navItem.href?.includes('#') ? navItem.href.split('#')[1] : null

              return (
                <Link
                  key={navItem.href}
                  {...navItem}
                  onClick={isCurrent && hash ? onHash(hash) : handleNavigation}
                  className={classNames('sub-nav-link', {
                    active: isCurrent && scrollSpyIndex === index,
                  })}
                  variant="subNav"
                >
                  {label}
                </Link>
              )
            })}
          </Stack>
        </Collapse>
      )}
    </StyledMobileMenu>
  )
}